import React from 'react';
import { EULA_TEXT, EULAheader, EULAInstructions, EULASectionText } from '../EULA';
import { HIPAASectionText } from '../HIPAA/hipaaSectionText';
import { HIPAA } from '../HIPAA/HIPAA';
import { Button } from '../buttons/Button';
import Checkbox from '../forms/Checkbox';
import { NavArrowRight, NavArrowDown } from 'iconoir-react';

/**
 * The PRE-GATE TERMS DISCLOSURE — CU-86ak6ez40, Phase D.
 *
 * Shown INSIDE `MfaRouteGate`, immediately before the factor choice, so a
 * patient reaching the enrolment gate sees what they are agreeing to before
 * they enrol. The CPO's ask (2026-08-27).
 *
 * ── ⚠️ WHAT THIS IS NOT ───────────────────────────────────────────────────
 * NOT an attestation, and it must never be called one in code, copy or
 * comments. It presents the terms and takes an acknowledgement; it produces NO
 * artifact and claims none. §164.520(c)(2) requires only a "good faith effort
 * to obtain written acknowledgment" of the notice, and HHS explicitly declined
 * to require a signature on the notice itself — which is what makes a
 * disclosure both defensible and cheap.
 *
 * ⚠️ NO SIGNATURE CANVAS. The legally-operative signature is taken post-AAL2 on
 * `info/consentForms`, unchanged and untouched by this work.
 *
 * ── ⚠️ IT READS NOTHING AND WRITES NOTHING ────────────────────────────────
 * No chart read, no chart write, no callable, no user-doc field. Every one of
 * the six measured break points on the existing consent page is a READ-BACK
 * (`PENDING_EULA_PREGATE.md`), and a gated patient cannot read the chart.
 * Taking no reads is what makes this screen work at AAL1 at all. Acknowledgement
 * lives in `sessionStorage` only — see `termsDisclosureAck.ts`.
 *
 * ── ⚠️ THE COPY IS IMPORTED, NEVER RESTATED ───────────────────────────────
 * `EULA_TEXT` comes from `@usacsmideo/types` (single source of truth,
 * CU-86aj11w66) and the HIPAA statements from `components/HIPAA/HIPAA.tsx` —
 * the SAME sources `info/consentForms.tsx` renders. A second copy of legal text
 * is a second thing to amend, and the one that gets missed.
 *
 * ⚠️ NOTE FOR REVIEW: `HIPAA.Statement` is a §164.508 AUTHORIZATION TO DISCLOSE
 * ("I authorize ... to use and to disclose"), not a §164.520 Notice of Privacy
 * Practices. This repo has no NPP text. The copy shown here is therefore the
 * authorization the patient will later SIGN — presented for reading, which is
 * honest — but if the intent was to disclose an NPP, that document does not yet
 * exist and this is not it. Flagged to Shaun 2026-08-31.
 */

export interface MfaTermsDisclosureProps {
  /** Advance to the factor choice. Called only when BOTH boxes are ticked. */
  onAcknowledge: () => void;
  /** ⚠️ The never-wedged escape, passed straight through from the gate. */
  onSignOut?: () => void;
}

export function MfaTermsDisclosure({ onAcknowledge, onSignOut }: MfaTermsDisclosureProps) {
  const [eulaOpen, setEulaOpen] = React.useState(false);
  const [hipaaOpen, setHipaaOpen] = React.useState(false);
  const [readEula, setReadEula] = React.useState(false);
  const [readHipaa, setReadHipaa] = React.useState(false);

  // ⚠️ BOTH, NOT EITHER. Two separate documents, two separate acknowledgements.
  // A single "I agree to the terms" box would let a patient acknowledge a
  // document they were never even offered.
  const bothAcknowledged = readEula && readHipaa;

  return (
    <div data-testid="mfa-terms-disclosure" className="w-full">
      <h1 className="text-2xl font-bold">Before you continue</h1>
      <p className="mt-2 text-sm">
        Please review the following documents. You will be asked to sign them once you have finished setting up your
        sign-in security.
      </p>

      <div id="disclosure-eula-area" className="mt-6">
        <div className="flex flex-row items-center">
          {eulaOpen ? (
            <NavArrowDown data-testid="disclosure-eula-toggle" onClick={() => setEulaOpen(!eulaOpen)} />
          ) : (
            <NavArrowRight data-testid="disclosure-eula-toggle" onClick={() => setEulaOpen(!eulaOpen)} />
          )}
          <p className="ml-2 text-xl font-bold">END USER LICENSE AGREEMENT</p>
        </div>
        {eulaOpen && (
          <div data-testid="disclosure-eula-body" className="h-[45vh] overflow-scroll">
            <EULAheader text={EULA_TEXT[0]} />
            <EULAInstructions text={EULA_TEXT[1]} />
            {/* ⚠️ Rendered from the array rather than 24 hand-written lines. The
                consent page enumerates each index with a per-index component
                choice; that shape is not reproducible generically, and this
                screen is a READING surface, not the signing one. Header/text
                distinction is preserved for the two structural cases above. */}
            {EULA_TEXT.slice(2).map((text: string, i: number) => (
              <EULASectionText key={`eula-${i}`} text={text} />
            ))}
          </div>
        )}
        <div className="mt-2">
          <Checkbox
            id="disclosure-eula-check"
            name="disclosure-eula-check"
            label="I have read the End User License Agreement."
            checked={readEula}
            onChange={() => setReadEula((v) => !v)}
          />
        </div>
      </div>

      <div id="disclosure-hipaa-area" className="mt-6">
        <div className="flex flex-row items-center">
          {hipaaOpen ? (
            <NavArrowDown data-testid="disclosure-hipaa-toggle" onClick={() => setHipaaOpen(!hipaaOpen)} />
          ) : (
            <NavArrowRight data-testid="disclosure-hipaa-toggle" onClick={() => setHipaaOpen(!hipaaOpen)} />
          )}
          <p className="ml-2 text-xl font-bold">HIPAA AUTHORIZATION</p>
        </div>
        {hipaaOpen && (
          <div data-testid="disclosure-hipaa-body" className="h-[45vh] overflow-scroll">
            <HIPAASectionText text={HIPAA.Statement.One} />
            <HIPAASectionText text={HIPAA.Statement.Two} />
            <HIPAASectionText text={HIPAA.Statement.Three} />
            <HIPAASectionText text={HIPAA.Statement.Four} />
            <HIPAASectionText text={HIPAA.Statement.Five} />
            <HIPAASectionText text={HIPAA.Statement.Six} />
            <HIPAASectionText text={HIPAA.Statement.Seven} />
            <HIPAASectionText text={HIPAA.Statement.Eight} />
          </div>
        )}
        <div className="mt-2">
          <Checkbox
            id="disclosure-hipaa-check"
            name="disclosure-hipaa-check"
            label="I have read the HIPAA authorization."
            checked={readHipaa}
            onChange={() => setReadHipaa((v) => !v)}
          />
        </div>
      </div>

      {/* ⚠️ QUERIED BY `id`, NOT `data-testid`. `ButtonProps` has no testid
          passthrough, so a `data-testid` here is silently DROPPED and any test
          asserting on it would query null — green for the wrong reason. */}
      <Button
        id="disclosure-continue"
        type="button"
        textContent="Continue"
        // ⚠️ DISABLED UNTIL BOTH. This is the entire enforcement surface of the
        // screen — there is no server-side check behind it, by design.
        disabled={!bothAcknowledged}
        onClick={onAcknowledge}
      />

      {/* ⚠️ THE NEVER-WEDGED ESCAPE, preserved through the new step. A patient
          must always be able to leave a screen they cannot satisfy. Without
          this, adding a step in front of the factor choice would REMOVE the
          escape that the choice itself provides. */}
      {onSignOut && (
        <div>
          <button type="button" data-testid="disclosure-sign-out" className="text-sm underline" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default MfaTermsDisclosure;
